"use client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Layout from "../../components/Layout"
import axios from "axios";
import currencyConfig from '../../utils/currencyConfig';
import { toast } from "react-toastify";
import { showSuccess } from "../../utils/toastUtils";
import { dispatchCartEvent, CART_EVENTS } from "../../utils/cartEvents";
import { offeringsAPI } from "../../services/api";



const PaymentOfferingPage = () => {
    const [isMobile, setIsMobile] = useState(false)
    const [isTablet, setIsTablet] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const navigate = useNavigate()
    const [showPrayerModal, setShowPrayerModal] = useState(false)

    const [offeringTypes, setOfferingTypes] = useState([]);
    const [selectedOffering, setSelectedOffering] = useState("");
    const [selectedOfferingData, setSelectedOfferingData] = useState(null); // Store full offering object
    const [amount, setAmount] = useState("");
    const [remarks, setRemarks] = useState("");
    const [loading, setLoading] = useState(false);
    const [basketCount, setBasketCount] = useState(0);



    // Dashboard cards data

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth
            setIsMobile(width <= 768)
            setIsTablet(width > 768 && width <= 1024)
        }

        checkScreenSize()
        fetchOfferingTypes();
        fetchBasketCount();
        window.addEventListener("resize", checkScreenSize)

        return () => {
            window.removeEventListener("resize", checkScreenSize)
        }
    }, [navigate])

    const fetchOfferingTypes = async () => {
        try {
            const token = localStorage.getItem("jwtToken");
            const API_URL = process.env.REACT_APP_API_URL || "/api";
            const response = await axios.get(`${API_URL}/offerings/types`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setOfferingTypes(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching offering types:", error);
        }
    };

    const fetchBasketCount = async () => {
        try {
            const token = localStorage.getItem("jwtToken");
            const API_URL = process.env.REACT_APP_API_URL || "/api";
            const response = await axios.get(`${API_URL}/offerings/basket`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setBasketCount(response.data.data.items?.length || 0);
            }
        } catch (error) {
            console.error("Error fetching basket count:", error);
        }
    };

    const addToBasket = async () => {
        const amt = parseFloat(amount);
        if (!selectedOffering || isNaN(amt) || amt <= 0) {
            toast.error("Please select an offering type and enter a valid amount greater than 0");
            return;
        }
        setLoading(true);
        try {
            // Use API service which automatically includes browser_id
            const response = await offeringsAPI.addToBasket({
                offering_type: selectedOffering,
                amount: amt,
                remarks: remarks,
                report_id: selectedOfferingData?.report_id || null,
            });

            if (response.success) {
                showSuccess("Item added to basket successfully!");

                // Dispatch cart event to update basket count in header immediately
                dispatchCartEvent(CART_EVENTS.ITEM_ADDED, {
                    offering: selectedOffering,
                    amount: amt
                });

                // Redirect to basket page
                setTimeout(() => {
                    navigate("/basket");
                }, 500);
            }
        } catch (error) {
            console.error("Error adding to basket:", error);
            toast.error("Failed to add item to basket");
        } finally {
            setLoading(false);
        }
    };

    const breadcrumb = (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "0.9rem" }}>
            <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/home")}
            >
                <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
            </svg>
            <span>/</span>
            <span style={{ cursor: "pointer" }} onClick={() => navigate("/home")}>Home</span>
            <span>/</span>
            <span>Other Payments & Offerings</span>
        </div>
    )

    const getSidebarWidth = () => {
        if (isMobile) return "80%"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentMargin = () => {
        if (isMobile) return "0"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentWidth = () => {
        if (isMobile) return "100%"
        return sidebarCollapsed ? "calc(100% - 80px)" : "calc(100% - 250px)"
    }

    const styles = {
        mainContent: {
            marginLeft: getMainContentMargin(),
            paddingTop: "80px",
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            width: getMainContentWidth(),
            transition: "margin-left 0.3s ease, width 0.3s ease",
        },
        breadcrumb: {
            display: "flex",
            alignItems: "center",
            marginBottom: "30px",
            color: "#6b7280",
            fontSize: "0.9rem",
            gap: "8px",
        },
        cardsGrid: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "20px",
            width: "100%",
            marginTop: "30px",
        },
        card: {
            backgroundColor: "#7fb3a3",
            borderRadius: "12px",
            padding: "10px",
            textAlign: "center",
            color: "white",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            width: "90%",
            margin: "0 auto",
            marginTop: "20px",
            transition: "all 0.3s ease",
        },
        cardIcon: {
            fontSize: "3rem",
            marginBottom: "20px",
            opacity: 0.9,
            display: "flex",
            justifyContent: "center",
        },
        cardTitle: {
            fontSize: "1.3rem",
            fontWeight: "600",
            marginBottom: "15px",
            lineHeight: "1.3",
        },
        cardDescription: {
            fontSize: "0.95rem",
            opacity: 0.9,
            lineHeight: "1.4",
        },
        error: {
            color: "#ef4444",
            fontSize: "0.9rem",
            textAlign: "center",
            marginBottom: "20px",
        },

        formContainer: {
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "40px",
            marginTop: "30px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxWidth: "1000px",
            margin: "100px auto",
            fontSize: "0.9rem",
        },
        formGrid: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
            gap: "20px",
            marginTop: "20px",
            width: "100%",
            alignItems: "end",
        },
        remarksContainer: {
            marginTop: "20px",
            width: "100%",
        },
        textarea: {
            width: "100%",
            padding: "12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "0.9rem",
            color: "#6b7280",
            boxSizing: "border-box",
            minHeight: "100px",
            resize: "vertical",
            fontFamily: "inherit",
        },
        formItem: {
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
        },
        select: {
            width: "100%",
            padding: "12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "0.9rem",
            backgroundColor: "white",
            color: "#6b7280",
            boxSizing: "border-box",
            appearance: "none",
            backgroundImage:
                "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
            backgroundPosition: "right 12px center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "16px",
        },
        inputContainer: {
            position: "relative",
            width: "100%",
        },
        currencySymbol: {
            position: "absolute",
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            color: "#6b7280",
            fontSize: "0.9rem",
        },
        input: {
            width: "100%",
            padding: "12px 12px 12px 28px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "0.9rem",
            color: "#6b7280",
            boxSizing: "border-box",
        },
        addButton: {
            backgroundColor: loading ? "#9ca3af" : "#6b7280",
            color: "white",
            border: "none",
            padding: "12px 28px",
            borderRadius: "4px",
            fontSize: "0.9rem",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
            whiteSpace: "nowrap",
        },
        basketStatus: {
            textAlign: "center",
            color: "#374151",
            fontSize: "1.1rem",
            fontWeight: "500",
            padding: "30px 20px",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            marginTop: "20px",
            marginBottom: "10px",
        },
    }

    return (
        <Layout
            activeMenuItem="Payments & Offerings"
            breadcrumb={breadcrumb}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
        >
            {/* Toast notifications will be shown via react-toastify */}
            <div style={styles.formContainer}>
                <div>
                    <div style={styles.formGrid}>
                        {/* Payments & Offerings Dropdown */}
                        <div style={styles.formItem}>
                            <label style={styles.label}>Payments & Offerings</label>
                            <select
                                value={selectedOffering}
                                onChange={(e) => {
                                    const selectedName = e.target.value;
                                    setSelectedOffering(selectedName);
                                    // Find and store the full offering object
                                    const offeringObj = offeringTypes.find(t => t.name === selectedName);
                                    setSelectedOfferingData(offeringObj || null);
                                    console.log('Selected offering:', selectedName, 'report_id:', offeringObj?.report_id);
                                }}
                                style={styles.select}
                            >
                                <option value="">Select an option</option>
                                {offeringTypes.map((type) => (
                                    <option key={type.id} value={type.name}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* Amount Field */}
                        <div style={{ ...styles.formItem, marginRight: 5 }}>
                            <label style={styles.label}>Amount</label>
                            <div style={styles.inputContainer}>
                                <span style={styles.currencySymbol}>{currencyConfig.symbol}</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Enter the amount"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Remarks Field - Moved below as a text area */}
                    <div style={styles.remarksContainer}>
                        <label style={styles.label}>Remarks</label>
                        <textarea
                            placeholder="Enter your remarks (max 100 characters)"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value.slice(0, 100))}
                            style={styles.textarea}
                            maxLength={100}
                            rows={4}
                        />
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', marginTop: '5px' }}>
                            {remarks.length}/100 characters
                        </div>
                    </div>
                    
                    {/* Add to Basket Button - Moved below the remarks */}
                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            onClick={addToBasket}
                            disabled={loading}
                            style={{
                                ...styles.addButton,
                                height: "41px",
                                width: isMobile ? '100%' : 'auto',
                                padding: '12px 40px',
                            }}
                            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = "#4b5563")}
                            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = "#6b7280")}
                        >
                            {loading ? "ADDING..." : "ADD TO BASKET"}
                        </button>
                    </div>
                </div>
                {/* Basket Status Message */}
                {basketCount > 0 && (
                    <div style={styles.basketStatus}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
                            <span>
                                You have {basketCount} {basketCount === 1 ? "item" : "items"} in your basket.
                            </span>
                            <button
                                onClick={() => navigate('/basket')}
                                style={{
                                    backgroundColor: "#7fb3a3",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 14px",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6a9b8e")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#7fb3a3")}
                            >
                                View Basket
                            </button>
                        </div>
                    </div>
                )}
            </div>

        </Layout>
    )
}

export default PaymentOfferingPage